import { useTranslation } from 'react-i18next';
import './AutoCompleteList.scss';
import { useRef, useEffect, forwardRef } from 'react';
import SuggestionGroupLabel from './suggestion-group-label/SuggestionGroupLabel';
import SearchSuggestion from './search-suggestion/SearchSuggestion';
import ContentSuggestion from './content-suggestion/ContentSuggestion';
import { useFilteredContentSuggestions } from './content-suggestion/useFilteredContentSuggestions';
import useSearchSuggestions from './search-suggestion/useSearchSuggestions';
import { useAppDispatch } from '../../../redux/hooks';
import { setSearchOption } from '../../../redux/store/content/slice';
import { SearchOption } from '../../../redux/store/content/types';

type Props = Readonly<{
  searchString: string;
  activeIndex: number;
  hidden?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onSuggestionClick?: () => void;
}>;

const AutoCompleteList = forwardRef<HTMLDivElement, Props>(
  (
    { searchString, activeIndex, hidden, onKeyDown, onSuggestionClick }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const contentSuggestions = useFilteredContentSuggestions(searchString);
    const searchSuggestions = useSearchSuggestions(searchString);
    const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
    const dispatch = useAppDispatch();

    // Focus the active item when the activeIndex changes
    useEffect(() => {
      if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
        itemRefs.current[activeIndex]?.focus();
      }
    }, [activeIndex]);

    const createHandleSearchSuggestionKeyDown =
      (searchOption: SearchOption) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          dispatch(setSearchOption(searchOption));
        }

        if (onKeyDown) {
          onKeyDown(e);
        }
      };

    const createHandleSearchSuggestionClick =
      (searchOption: SearchOption) => () => {
        dispatch(setSearchOption(searchOption));

        if (onSuggestionClick) {
          onSuggestionClick();
        }
      };

    return (
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- custom implementation
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        id='auto-complete-list'
        role='listbox'
        hidden={hidden}
        aria-hidden={hidden}
        aria-activedescendant={
          activeIndex >= 0 ? itemRefs.current[activeIndex]?.id : undefined
        }
        tabIndex={-1}
        className='auto-complete-list border border-light'>
        {contentSuggestions.length > 0 && (
          <>
            <SuggestionGroupLabel
              id='content-option-group-label'
              text={t('linkSuggestionsAutoCompleteListHeaderHeader')}
            />
            {contentSuggestions.map((option, idx) => (
              <ContentSuggestion
                key={option.treeElement.id}
                ref={(el: HTMLAnchorElement | null) => {
                  itemRefs.current[idx] = el;
                }}
                id={option.treeElement.id}
                value={option}
                ariaDescribedBy='content-option-group-label'
                ariaSelected={activeIndex === idx}
                tabIndex={activeIndex === idx ? 0 : -1}
                onKeyDown={onKeyDown}
                onClick={onSuggestionClick}
              />
            ))}
          </>
        )}
        <SuggestionGroupLabel
          id='search-suggestion-group-label'
          text={t('searchSuggestionsAutoCompleteListHeaderHeader')}
        />
        {searchSuggestions.map((s, idx) => (
          <SearchSuggestion
            key={s.href}
            id={`search-suggestion-${idx}`}
            label={s.label}
            href={s.href}
            ariaSelected={activeIndex === contentSuggestions.length + idx}
            ariaDescribedBy='search-suggestion-group-label'
            tabIndex={activeIndex === contentSuggestions.length + idx ? 0 : -1}
            ref={(el: HTMLAnchorElement | null) => {
              itemRefs.current[contentSuggestions.length + idx] = el;
            }}
            onKeyDown={createHandleSearchSuggestionKeyDown(s.searchOption)}
            onClick={createHandleSearchSuggestionClick(s.searchOption)}
          />
        ))}
      </div>
    );
  },
);

export default AutoCompleteList;
